import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { apiService } from '../../services/api';
import type {
  Plan,
  PriceListGroup,
  ServerBillingToken,
  ServerDetailsResponse,
  ServerPricelistGroupAssignmentsUpdate,
} from '../../types';

type ServerTab = 'overview' | 'security' | 'pricelists' | 'website';
type GroupSelection = Record<number, { selected: boolean; sortOrder: number }>;

function buildSelection(
  groups: PriceListGroup[],
  details: ServerDetailsResponse | null,
): GroupSelection {
  const assigned = new Map(
    (details?.pricelist_groups ?? []).map((group) => [
      group.group_id,
      Number(group.sort_order ?? 0),
    ]),
  );

  return Object.fromEntries(
    groups.map((group) => [
      group.group_id,
      {
        selected: assigned.has(group.group_id),
        sortOrder: assigned.get(group.group_id) ?? Number(group.sort_order ?? 0),
      },
    ]),
  );
}

const ServerDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<ServerTab>('overview');
  const [availablePlans, setAvailablePlans] = useState<Plan[]>([]);
  const [details, setDetails] = useState<ServerDetailsResponse | null>(null);
  const [allGroups, setAllGroups] = useState<PriceListGroup[]>([]);
  const [selection, setSelection] = useState<GroupSelection>({});
  const [billingToken, setBillingToken] = useState<ServerBillingToken | null>(null);
  const [copiedToken, setCopiedToken] = useState(false);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [savingPricelists, setSavingPricelists] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [detailsResponse, priceListGroups, plansResponse] = await Promise.all([
        apiService.servers.getServerDetails(id),
        apiService.getPriceListGroups(),
        apiService.getAllPlans(200),
      ]);
      setDetails(detailsResponse);
      setAllGroups(priceListGroups);
      setAvailablePlans(plansResponse);
      setSelection(buildSelection(priceListGroups, detailsResponse));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load server details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [id]);

  const handleRegenerateCredentials = async () => {
    if (!id) {
      return;
    }
    if (!window.confirm('Regenerate API credentials for this server?')) {
      return;
    }

    try {
      setWorking(true);
      setError(null);
      await apiService.servers.regenerateServerCredentials(id);
      setBillingToken(null);
      setCopiedToken(false);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to regenerate credentials.');
    } finally {
      setWorking(false);
    }
  };

  const handleGenerateBillingToken = async () => {
    if (!id) {
      return;
    }

    try {
      setWorking(true);
      setError(null);
      const token = await apiService.servers.generateBillingBackendToken(id);
      setBillingToken(token);
      setCopiedToken(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate billing token.');
    } finally {
      setWorking(false);
    }
  };

  const handleCopyBillingToken = async () => {
    if (!billingToken) {
      return;
    }
    try {
      await navigator.clipboard.writeText(billingToken.access_token);
      setCopiedToken(true);
    } catch (err) {
      setError('Failed to copy billing token.');
    }
  };

  const handleGroupToggle = (groupId: number, selected: boolean) => {
    setSelection((current) => ({
      ...current,
      [groupId]: {
        selected,
        sortOrder: current[groupId]?.sortOrder ?? 0,
      },
    }));
  };

  const handleSortOrderChange = (groupId: number, sortOrder: number) => {
    setSelection((current) => ({
      ...current,
      [groupId]: {
        selected: current[groupId]?.selected ?? false,
        sortOrder,
      },
    }));
  };

  const handleSavePricelists = async () => {
    if (!id) {
      return;
    }

    const payload: ServerPricelistGroupAssignmentsUpdate = {
      pricelistGroups: Object.entries(selection)
        .filter(([, value]) => value.selected)
        .map(([groupId, value]) => ({
          groupId: Number(groupId),
          sortOrder: Number(value.sortOrder || 0),
        }))
        .sort((left, right) => left.sortOrder - right.sortOrder || left.groupId - right.groupId),
    };

    try {
      setSavingPricelists(true);
      setError(null);
      const updated = await apiService.servers.updateServerPricelistGroups(id, payload);
      setDetails(updated);
      setSelection(buildSelection(allGroups, updated));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save assigned pricelists.');
    } finally {
      setSavingPricelists(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-slate-500">Loading server details...</div>;
  }

  if (!details) {
    return <div className="p-8 text-center text-slate-500">Server details are unavailable.</div>;
  }

  const server = details.server;
  const groups = [...allGroups].sort((left, right) => {
    const leftSelection = selection[left.group_id];
    const rightSelection = selection[right.group_id];
    const leftRank = leftSelection?.selected ? 0 : 1;
    const rightRank = rightSelection?.selected ? 0 : 1;
    if (leftRank !== rightRank) {
      return leftRank - rightRank;
    }
    const leftOrder = Number(leftSelection?.sortOrder ?? left.sort_order ?? 0);
    const rightOrder = Number(rightSelection?.sortOrder ?? right.sort_order ?? 0);
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }
    return left.group_id - right.group_id;
  });

  return (
    <div className="mx-auto max-w-7xl px-6 py-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to="/servers" className="text-sm font-medium text-slate-600 hover:text-slate-900">
            Back to servers
          </Link>
          <h1 className="mt-2 text-3xl font-semibold text-slate-900">{server.name}</h1>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">
            Review server credentials and control which pricelist groups are transferred to the
            website. Sort numbers in the pricelist tab define the comparison column order for this
            server scope.
          </p>
        </div>

        <button type="button" className="btn btn-secondary" onClick={loadData} disabled={working}>
          Refresh
        </button>
      </div>

      {error && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error}
        </div>
      )}

      <div className="mb-6 flex gap-3">
        {(['overview', 'security', 'pricelists', 'website'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            className={`rounded-full border px-4 py-2 text-sm font-semibold ${
              activeTab === tab
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-300 bg-white text-slate-700'
            }`}
            onClick={() => setActiveTab(tab)}
          >
            {tab[0].toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-slate-900">Server</h2>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-slate-500">Host</dt>
                <dd className="mt-1 font-medium text-slate-900">{server.host}</dd>
              </div>
              <div>
                <dt className="text-sm text-slate-500">Status</dt>
                <dd className="mt-1 font-medium text-slate-900">
                  {server.is_active ? 'Active' : 'Inactive'}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-slate-500">Created</dt>
                <dd className="mt-1 font-medium text-slate-900">
                  {new Date(server.created_at).toLocaleString()}
                </dd>
              </div>
            </dl>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="mb-4 text-lg font-semibold text-slate-900">Assigned Pricelists</h2>
            <div className="mb-4 flex gap-8">
              <div>
                <div className="text-3xl font-semibold text-slate-900">
                  {details.pricelist_groups.length}
                </div>
                <div className="text-sm text-slate-500">Assigned groups</div>
              </div>
              <div>
                <div className="text-3xl font-semibold text-slate-900">
                  {details.pricelist_groups.filter((group) => group.is_active).length}
                </div>
                <div className="text-sm text-slate-500">Active groups</div>
              </div>
            </div>

            <div className="space-y-3">
              {details.pricelist_groups.length > 0 ? (
                details.pricelist_groups.map((group) => (
                  <div
                    key={group.group_id}
                    className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3"
                  >
                    <div>
                      <div className="font-medium text-slate-900">{group.name}</div>
                      <div className="text-sm text-slate-500">
                        {group.currency} · {group.country || 'Global'}
                      </div>
                    </div>
                    <code className="text-sm text-slate-700">Sort {group.sort_order ?? 0}</code>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">
                  No pricelist groups are assigned to this server.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'security' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="mb-2 text-lg font-semibold text-slate-900">Keys and Secrets</h2>
          <p className="mb-6 text-sm text-slate-600">
            Use this tab to rotate API credentials and generate a new
            `BILLING_BACKEND_BEARER_TOKEN` for the current source URL.
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="mb-2 text-sm font-medium text-slate-600">API Key</div>
              <code className="block rounded-xl bg-slate-950 p-4 text-xs text-slate-100">
                {server.api_key || '-'}
              </code>
            </div>
            <div>
              <div className="mb-2 text-sm font-medium text-slate-600">API Secret</div>
              <code className="block rounded-xl bg-slate-950 p-4 text-xs text-slate-100">
                {server.api_secret || '-'}
              </code>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void handleRegenerateCredentials()}
              disabled={working}
            >
              {working ? 'Working...' : 'Regenerate Credentials'}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void handleGenerateBillingToken()}
              disabled={working}
            >
              {working ? 'Working...' : 'Generate Billing Token'}
            </button>
          </div>

          {billingToken && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-medium text-slate-900">BILLING_BACKEND_BEARER_TOKEN</div>
                  <div className="text-sm text-slate-500">
                    Expires:{' '}
                    {billingToken.expires_at
                      ? new Date(billingToken.expires_at).toLocaleString()
                      : 'n/a'}
                  </div>
                </div>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleCopyBillingToken}>
                  {copiedToken ? 'Copied' : 'Copy'}
                </button>
              </div>
              <code className="block rounded-xl bg-slate-950 p-4 text-xs text-slate-100">
                {billingToken.access_token}
              </code>
            </div>
          )}
        </div>
      )}

      {activeTab === 'pricelists' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Assigned Pricelist Groups</h2>
              <p className="mt-2 max-w-3xl text-sm text-slate-600">
                Select the pricelist groups that this server may transfer. Unselected groups are
                excluded. Lower sort numbers appear earlier in the website comparison matrix.
              </p>
            </div>

            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void handleSavePricelists()}
              disabled={savingPricelists}
            >
              {savingPricelists ? 'Saving...' : 'Save Pricelists'}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="data-table w-full min-w-[860px] border-collapse">
              <thead>
                <tr>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-xs uppercase tracking-wide text-slate-500">
                    Assign
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-xs uppercase tracking-wide text-slate-500">
                    Pricelist Group
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-xs uppercase tracking-wide text-slate-500">
                    Scope
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-xs uppercase tracking-wide text-slate-500">
                    Periods
                  </th>
                  <th className="border-b border-slate-200 px-4 py-3 text-left text-xs uppercase tracking-wide text-slate-500">
                    Sort Number
                  </th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => {
                  const current = selection[group.group_id] ?? {
                    selected: false,
                    sortOrder: Number(group.sort_order ?? 0),
                  };

                  return (
                    <tr key={group.group_id}>
                      <td className="border-b border-slate-100 px-4 py-4 align-top">
                        <input
                          type="checkbox"
                          checked={current.selected}
                          onChange={(event) =>
                            handleGroupToggle(group.group_id, event.target.checked)
                          }
                        />
                      </td>
                      <td className="border-b border-slate-100 px-4 py-4 align-top">
                        <div className="font-medium text-slate-900">{group.name}</div>
                        <div className="text-sm text-slate-500">Group ID: {group.group_id}</div>
                      </td>
                      <td className="border-b border-slate-100 px-4 py-4 align-top text-sm text-slate-600">
                        <div>Plan ID: {group.plan_id}</div>
                        <div>{group.currency}</div>
                        <div>{group.country || 'Global'}</div>
                      </td>
                      <td className="border-b border-slate-100 px-4 py-4 align-top text-sm text-slate-600">
                        {group.periods.map((period) => `${period.months}m`).join(', ')}
                      </td>
                      <td className="border-b border-slate-100 px-4 py-4 align-top">
                        <input
                          type="number"
                          min={0}
                          className="form-control w-28"
                          value={current.sortOrder}
                          disabled={!current.selected}
                          onChange={(event) =>
                            handleSortOrderChange(group.group_id, Number(event.target.value || 0))
                          }
                        />
                      </td>
                    </tr>
                  );
                })}
                {groups.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-500">
                      No pricelist groups are available.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'website' && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-slate-900">Website Default Settings</h2>
            <p className="mt-2 text-sm text-slate-600">
              Configure the default plan and billing period that website users will see when they first visit the pricing page.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Default Plan (Most Chosen)
              </label>
              <select
                className="form-control w-full"
                value={details.server.default_plan_id || ''}
                onChange={async (e) => {
                  const planId = e.target.value === '' ? null : Number(e.target.value);
                  try {
                    await apiService.servers.updateServer(details.server.id, {
                      default_plan_id: planId
                    });
                    setDetails({...details, server: {...details.server, default_plan_id: planId}});
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to update default plan.');
                  }
                }}
              >
                <option value="">-- Select a default plan --</option>
                {availablePlans.map((plan) => (
                  <option key={plan.plan_id} value={plan.plan_id}>
                    {plan.name}
                  </option>
                ))}
              </select>
              {details.server.default_plan_id && (
                <p className="mt-2 text-xs text-slate-500">
                  This plan will be shown as "Most Chosen" on the website and selected by default.
                </p>
              )}
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Default Billing Period
              </label>
              <select
                className="form-control w-full"
                value={details.server.default_billing_period || ''}
                onChange={async (e) => {
                  const period = e.target.value === '' ? null : e.target.value;
                  try {
                    await apiService.servers.updateServer(details.server.id, {
                      default_billing_period: period
                    });
                    setDetails({...details, server: {...details.server, default_billing_period: period}});
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to update default billing period.');
                  }
                }}
              >
                <option value="">-- Select a default period --</option>
                {[
                  {value: '1_month', label: 'Monthly'},
                  {value: '3_months', label: 'Quarterly'},
                  {value: '6_months', label: 'Semi-annually'},
                  {value: '12_months', label: 'Annually'},
                  {value: '24_months', label: 'Bi-annually'},
                ].map((period) => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))}
              </select>
              {details.server.default_billing_period && (
                <p className="mt-2 text-xs text-slate-500">
                  Website users will see this billing period selected by default.
                </p>
              )}
            </div>
          </div>

          <div className="mt-8 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <h3 className="mb-3 text-sm font-semibold text-slate-900">How This Works</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex items-start">
                <span className="mr-2 mt-0.5 h-2 w-2 rounded-full bg-brand-500 flex-shrink-0"></span>
                <span>The default plan will display with a "Most Chosen" badge on the website</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 mt-0.5 h-2 w-2 rounded-full bg-brand-500 flex-shrink-0"></span>
                <span>The default billing period will be pre-selected in the pricing slider</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 mt-0.5 h-2 w-2 rounded-full bg-brand-500 flex-shrink-0"></span>
                <span>Users can still change their selection, but these defaults guide their initial experience</span>
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServerDetails;
